import Colors from '@constants/Colors';
import { ADD_COMMISSION_PRO } from '@gql/commissions';
import { FormControl, FormHelperText, FormLabel as MuiFormLabel, styled } from '@mui/material';
import * as React from 'react';
import { Controller } from 'react-hook-form';

import { AutoCompleteField } from './AutoCompleteField';
import { AutoCompleteSingleField } from './AutoCompleteSingleField';
import { NestedSubOption, Props } from './types';
import { flatten } from './utils';

export type { NestedSubOption, NestedOption, Props } from './types';

const FormLabel = styled(MuiFormLabel)(() => ({
    marginBottom: '4px',
}));

export function AutocompleteCommissions({
    control,
    name,
    label,
    placeholder,
    disabled,
    options,
    groupedOptions,
    defaultValue,
    multiple = true,
    ...props
}: Props & { multiple?: boolean }) {
    const _options = React.useMemo(() => {
        return (groupedOptions ? flatten(groupedOptions) : options) || [];
    }, [groupedOptions, options]);

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={multiple ? defaultValue || [] : defaultValue || null}
            rules={props.rules}
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                <FormControl sx={{ flex: 1, minWidth: 200 }} disabled={disabled} fullWidth error={!!error}>
                    <FormLabel htmlFor={name}>
                        {label}
                    </FormLabel>
                    {multiple ? (
                        <AutoCompleteField
                            ref={ref}
                            viewOnly={props.viewOnly}
                            options={_options}
                            value={value}
                            onChange={val => {
                                onChange(val);
                                props.onChange?.(val);
                            }}
                            label={label}
                            mutation={ADD_COMMISSION_PRO}
                            placeholder={placeholder}
                            disabled={disabled}
                            allowAdd={props.allowAdd}
                            multiple={multiple}
                            groupBy={groupedOptions ? (option: NestedSubOption) => option?.parent?.name : undefined}
                            error={!!error}
                            newValueResolver={(data: any, dialogValue: NestedSubOption): NestedSubOption => {
                                return {
                                    name: dialogValue.name,
                                    id: data.name,
                                    icon: undefined,
                                    parent: { name: 'Autre', id: data.profession?._id || 'other' },
                                };
                            }} />
                    ) : (
                        <AutoCompleteSingleField
                            ref={ref}
                            viewOnly={props.viewOnly}
                            options={_options}
                            value={value}
                            onChange={val => {
                                onChange(val);
                                props.onChange?.(val);
                            }}
                            label={label}
                            mutation={ADD_COMMISSION_PRO}
                            refetchQueries={props.refetchQueries}
                            placeholder={placeholder}
                            disabled={disabled}
                            allowAdd={props.allowAdd}
                            groupBy={groupedOptions ? (option: NestedSubOption) => option?.parent?.name : undefined}
                            error={!!error}
                            newValueResolver={(data: any, dialogValue: NestedSubOption): NestedSubOption => {
                                return {
                                    name: dialogValue.name,
                                    id: data.name,
                                    icon: undefined,
                                    parent: { name: 'Autre', id: data.profession?._id || 'other' },
                                };
                            }}
                        />
                    )}

                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                </FormControl>
            )}
        />
    );
}
